import { Ban, BookLock, CheckCircle2, ShieldCheck, Siren } from "lucide-react";
import type { ComponentType } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { GenerationResult, GuardrailEntry } from "@/lib/api";
import { cn } from "@/lib/utils";

import { TierBadge } from "./tier-badge";
import type { GuardrailOutcome, Tier } from "./types";
import { formatPageRanges, OUTCOME_LABEL, OUTCOME_STYLE } from "./utils";

// Blocked first, then escalated: the two outcomes a market has to act on.
const OUTCOME_ORDER: GuardrailOutcome[] = ["blocked", "escalated", "applied"];

const OUTCOME_ICON: Record<GuardrailOutcome, ComponentType<{ className?: string }>> = {
  applied: CheckCircle2,
  blocked: Ban,
  escalated: Siren,
};

const TIER_TILE_LABEL: Record<Tier, string> = {
  1: "automated",
  2: "flagged",
  3: "escalated",
};

interface GuardrailReportProps {
  generation: GenerationResult | undefined;
  isLoading: boolean;
  onOpenRules: () => void;
}

export function GuardrailReport({ generation, isLoading, onOpenRules }: GuardrailReportProps) {
  const entries = generation?.guardrail_report ?? [];
  const pagesKept = generation?.pages_kept ?? [];
  const grouped = OUTCOME_ORDER.map((outcome) => ({
    outcome,
    items: entries.filter((entry) => entry.outcome === outcome),
  })).filter((group) => group.items.length > 0);

  return (
    <section className="border-border bg-card rounded-2xl border shadow-sm">
      <header className="border-border flex items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-velux h-4 w-4" />
          <h2 className="font-heading text-foreground text-sm font-semibold">
            Guardrail &amp; control report
          </h2>
        </div>
        <Button variant="ghost" size="sm" onClick={onOpenRules} className="h-7 px-2 text-xs">
          <BookLock className="h-3.5 w-3.5" />
          Rules
        </Button>
      </header>

      {isLoading ? (
        <div className="space-y-3 p-4">
          <Skeleton className="bg-border/60 h-12 w-full" />
          <div className="grid grid-cols-3 gap-2">
            <Skeleton className="bg-border/60 h-14" />
            <Skeleton className="bg-border/60 h-14" />
            <Skeleton className="bg-border/60 h-14" />
          </div>
          <Skeleton className="bg-border/60 h-10 w-full" />
          <Skeleton className="bg-border/60 h-10 w-full" />
        </div>
      ) : !generation ? (
        <p className="text-muted p-4 text-xs leading-6">
          Nothing produced yet. Once an artifact is built, every rule that was applied, everything
          that was blocked and every change escalated to the Strategic Comms team is listed here.
        </p>
      ) : (
        <div className="space-y-4 p-4">
          <p className="border-velux/25 bg-velux-tint/60 text-foreground rounded-lg border px-3 py-2 text-xs leading-5 font-medium">
            {generation.headline}
          </p>

          {pagesKept.length > 0 && (
            <p className="text-foreground/75 text-xs leading-5">
              <span className="font-semibold">Pages kept:</span> {formatPageRanges(pagesKept)} (
              {pagesKept.length} of the source document)
            </p>
          )}

          <div className="grid grid-cols-3 gap-2">
            {([1, 2, 3] as Tier[]).map((tier) => (
              <div key={tier} className="border-border bg-surface-muted/60 rounded-lg border p-2">
                <TierBadge tier={tier} compact />
                <p className="text-foreground mt-1.5 text-lg leading-none font-bold tabular-nums">
                  {tierCount(generation, tier)}
                </p>
                <p className="text-muted text-[10px] tracking-wide uppercase">
                  {TIER_TILE_LABEL[tier] ?? ""}
                </p>
              </div>
            ))}
          </div>

          {entries.length === 0 ? (
            <p className="text-muted text-xs">No rules fired for this run.</p>
          ) : (
            // The card grows to fit its findings rather than scrolling inside
            // a fixed box. A run that escalated ten items has ten things the
            // market has to read, and a 20rem window cut the last of them off
            // at the panel's own border — which read as the report being
            // broken rather than as there being more to see.
            <div className="space-y-3">
              {grouped.map((group) => (
                <OutcomeGroup key={group.outcome} outcome={group.outcome} items={group.items} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

interface OutcomeGroupProps {
  outcome: GuardrailOutcome;
  items: GuardrailEntry[];
}

function OutcomeGroup({ outcome, items }: OutcomeGroupProps) {
  const style = OUTCOME_STYLE[outcome] ?? OUTCOME_STYLE.applied;
  const Icon = OUTCOME_ICON[outcome] ?? CheckCircle2;

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className={cn("h-2 w-2 rounded-full", style.dot)} />
        <h3 className="text-foreground text-[11px] font-bold tracking-[0.12em] uppercase">
          {OUTCOME_LABEL[outcome] ?? outcome}
        </h3>
        <span className="text-muted text-[11px] tabular-nums">({items.length})</span>
      </div>
      <ul className="space-y-1.5">
        {items.map((entry) => (
          <li
            key={`${entry.rule_id}-${entry.summary}`}
            className={cn("flex items-start gap-2 rounded-lg border px-2.5 py-2", style.row)}
          >
            <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", style.icon)} />
            <div className="min-w-0 flex-1">
              <p className="text-foreground text-xs leading-5 font-medium">{entry.summary}</p>
              {entry.detail && (
                <p className="text-foreground/70 mt-0.5 text-[11px] leading-5">{entry.detail}</p>
              )}
              <div className="mt-1 flex items-center gap-1.5">
                <Badge
                  variant="outline"
                  className="border-border/70 text-muted bg-card/60 font-mono text-[10px]"
                >
                  {entry.rule_id}
                </Badge>
                <span className="text-muted text-[10px] tracking-wide uppercase">
                  {entry.category.replace(/_/g, " ")}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function tierCount(generation: GenerationResult, tier: Tier): number {
  const summary = generation.tier_summary;
  if (tier === 1) {
    return summary.tier1 ?? 0;
  }
  if (tier === 2) {
    return summary.tier2 ?? 0;
  }
  return summary.tier3 ?? 0;
}
